import { FC } from 'react';
import { groupBy } from 'lodash';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import Account from './Account';
import { type Section } from './types';
import getColumns from './getColumns';
import AccountingTable from '../components/AccountingTable';
import AccountingSection from '../components/AccountingSection';

type Props = {
  sectionTitle: string;
  section?: Section;
  isLoading: boolean;
  baseCurrency: CurrencyCode;
};

const SectionContent: FC<Props> = ({
  sectionTitle,
  section = {
    transactions: [],
    total: 0,
  },
  isLoading,
  baseCurrency,
}) => {
  const columns = getColumns({ baseCurrency });
  const hasTransactions = section.transactions.length > 0;

  const transactionsByAccountId = groupBy(section.transactions, 'accountId');
  const accounts = Object.keys(transactionsByAccountId);

  return (
    <AccountingSection
      title={sectionTitle}
      total={numberToCurrencyString(section.total, {
        currency: baseCurrency,
        negativeInBrackets: true,
      })}
      customRow={
        <AccountingTable
          columns={columns}
          rows={[]}
          isLoading={isLoading}
          onRowExpand={() => {}} // Simply to display actions column to match the account table width
          borderless
          hideHeader
          showTopBorder
          displayFooterIfEmptyTable
          externalScroll
          customFooterRow={[
            {
              key: 'total',
              content: (
                <Typography variant="h6" fontWeight={700}>
                  {sectionTitle} total
                </Typography>
              ),
              colspan: 5,
              backgroundColor: grey[100],
            },
            {
              key: 'totalAmount',
              content: (
                <Typography variant="h6" fontWeight={700}>
                  {numberToCurrencyString(section.total, {
                    currency: baseCurrency,
                    negativeInBrackets: true,
                  })}
                </Typography>
              ),
              colspan: 6,
              backgroundColor: grey[100],
            },
          ]}
        />
      }
    >
      {isLoading || hasTransactions ? (
        accounts.map((account, index) => (
          <Account
            isLoading={isLoading}
            key={account}
            accountLabel={transactionsByAccountId[account][0].accountLabel}
            transactions={transactionsByAccountId[account]}
            baseCurrency={baseCurrency}
            isLast={index === accounts.length - 1}
          />
        ))
      ) : (
        <AccountingTable
          columns={columns}
          rows={section.transactions}
          emptyMessage="You have no transactions."
          borderless
          externalScroll
          bodyRowPaddingY={30}
        />
      )}
    </AccountingSection>
  );
};

export default SectionContent;
